import { datadogRum } from '@datadog/browser-rum';
import { CONTEXT_NAMES } from '@src/tokens/datadog';

export const DatadogTradingEntity = {
  Order: 'order',
  Quote: 'quote',
  CustomerOrder: 'customer_order',
  CustomerQuote: 'customer_quote',
  CareOrder: 'care_order',
} as const;
export type DatadogTradingEntity = (typeof DatadogTradingEntity)[keyof typeof DatadogTradingEntity];

export const DatadogTradingAction = {
  Submit: 'submit',
  Modify: 'modify',
  Cancel: 'cancel',
  Pause: 'pause',
  Resume: 'resume',
  RequestQuote: 'request_quote',
  RequestImprovement: 'request_improvement',
  AcceptQuote: 'accept_quote',
  SendQuote: 'send_quote',
  RejectQuote: 'reject_quote',
  LockQuoteSpread: 'lock_quote_spread',
  LockQuotePrice: 'lock_quote_price',
  ModifySpread: 'modify_spread',
  ForceFill: 'force_fill',
  ForceCancel: 'force_cancel',
} as const;
export type DatadogTradingAction = (typeof DatadogTradingAction)[keyof typeof DatadogTradingAction];

interface DatadogTradingActionContext {
  clOrdID?: string;
  orderID?: string;
}

interface TrackTradingActionParams extends DatadogTradingActionContext {
  entity: DatadogTradingEntity;
  action: DatadogTradingAction;
}

const ENTITY_LABELS = {
  [DatadogTradingEntity.Order]: 'Order',
  [DatadogTradingEntity.Quote]: 'Quote',
  [DatadogTradingEntity.CustomerOrder]: 'Customer Order',
  [DatadogTradingEntity.CustomerQuote]: 'Customer Quote',
  [DatadogTradingEntity.CareOrder]: 'Care Order',
} as const satisfies Record<DatadogTradingEntity, string>;

const ACTION_LABELS = {
  [DatadogTradingAction.Submit]: 'Submit',
  [DatadogTradingAction.Modify]: 'Modify',
  [DatadogTradingAction.Cancel]: 'Cancel',
  [DatadogTradingAction.Pause]: 'Pause',
  [DatadogTradingAction.Resume]: 'Resume',
  [DatadogTradingAction.RequestQuote]: 'Request',
  [DatadogTradingAction.RequestImprovement]: 'Request Improvement',
  [DatadogTradingAction.AcceptQuote]: 'Accept',
  [DatadogTradingAction.SendQuote]: 'Send',
  [DatadogTradingAction.RejectQuote]: 'Reject',
  [DatadogTradingAction.LockQuoteSpread]: 'Lock Spread',
  [DatadogTradingAction.LockQuotePrice]: 'Lock Price',
  [DatadogTradingAction.ModifySpread]: 'Modify Spread',
  [DatadogTradingAction.ForceFill]: 'Force Fill',
  [DatadogTradingAction.ForceCancel]: 'Force Cancel',
} as const satisfies Record<DatadogTradingAction, string>;

function getTradingActionName({ entity, action }: TrackTradingActionParams): string {
  return `${ACTION_LABELS[action]} ${ENTITY_LABELS[entity]}`;
}

export function trackTradingAction({ entity, action, clOrdID, orderID }: TrackTradingActionParams): void {
  datadogRum.addAction(getTradingActionName({ entity, action, clOrdID, orderID }), {
    [CONTEXT_NAMES.TRADING_ENTITY]: entity,
    [CONTEXT_NAMES.TRADING_ACTION]: action,
    ...(clOrdID ? { [CONTEXT_NAMES.CL_ORD_ID]: clOrdID } : {}),
    ...(orderID ? { [CONTEXT_NAMES.ORDER_ID]: orderID } : {}),
  });
}

export function trackOrderAction(action: DatadogTradingAction, context: DatadogTradingActionContext = {}): void {
  trackTradingAction({ entity: DatadogTradingEntity.Order, action, ...context });
}

export function trackQuoteAction(action: DatadogTradingAction, context: DatadogTradingActionContext = {}): void {
  trackTradingAction({ entity: DatadogTradingEntity.Quote, action, ...context });
}

export function trackCustomerOrderAction(
  action: DatadogTradingAction,
  context: DatadogTradingActionContext = {}
): void {
  trackTradingAction({ entity: DatadogTradingEntity.CustomerOrder, action, ...context });
}

export function trackCustomerQuoteAction(
  action: DatadogTradingAction,
  context: DatadogTradingActionContext = {}
): void {
  trackTradingAction({ entity: DatadogTradingEntity.CustomerQuote, action, ...context });
}

export function trackCareOrderAction(action: DatadogTradingAction, context: DatadogTradingActionContext = {}): void {
  trackTradingAction({ entity: DatadogTradingEntity.CareOrder, action, ...context });
}
